import type {
  Id,
  TimelineGroup,
  TimelineItem,
} from './react-calendar-timeline';
import type {
  PortCallEventType,
  SingleEventType,
  StaticEvent,
  TimeData,
} from '../core/TimeData';
import {
  type Leg,
  type OffHireEvent,
  type PortCallEvent,
  type SingleEvent,
  type WaitEvent,
} from '../core/TimeData';
import type {
  DocumentType as PortCallDocument,
  OpenPositionsFragment,
  ScenarioTimeline_CoaFragment,
  VesselReservationFragment,
} from '@seaber/common/graphql/types';
import type { CSSProperties } from 'react';
import type { Suggestion } from '../core/suggestions/getSuggestions';
import type { TCOrderEntry } from '@seaber/common/core/entries';
import type { Vessel } from '../core/Vessel';
import type { EventTimeline } from '../core/Timelines';
import type { ScenarioOrder } from '../../lib/planningData';
import type { TimelineDiffState } from '../ScenarioUtil';
import type { ScenarioType } from './ScenarioMergeTimeline';
import type { computeTceForTimeData } from '../shipment/TCE';
import type { VoyagePerformanceMetrics } from '../core/Core.voyage-metrics';
import type { ScenarioTimelineProps } from './ScenarioTimeline';
import type { IncomingContractType } from '../order/OrderTableUtils';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { DefaultMap } from '@seaber/common/lib/collections';

export enum ItemType {
  SCHEDULED_ORDER = 'TC',
  EVENT = 'SE',
  SUGGESTION = 'SUGG',
  OFFHIRE = 'O',
  SPOT = 'S',
  COA = 'C',
  OPEN = 'OPEN',
  RESERVATION = 'RESERVE',
  COMPLETED = '$',
  TC_IN = 'TC_IN',
}

type TimelineItemBase = {
  groupObj?: TimelineGroup<ScenarioTimelineGroupFields>;
  zIndex?: CSSProperties['zIndex'] | undefined;
  width?: number;
  isCompressedView?: ScenarioTimelineProps['isCompressedView'];
} & (
  | {
      isShadow: true;
      originalItemId: Id;
      originalContract?: string;
    }
  | {
      isShadow?: false;
    }
);

export type TimelineVoyage = TimelineItemBase & {
  type: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER;
  vessel?: Vessel;
  orders: ScenarioOrder[];
  events: TimeData[];
  implicitEvents: (Leg | WaitEvent)[];
  isCollapsed: boolean; // A collapsed item is intact and may contain several orders, if not collapsed then this item is the result of de-collapsing a shipment. Should affect context menu options.
  selected?: boolean;
  isShared: boolean; // Is every event in the batch shared
  zIndex?: CSSProperties['zIndex'] | undefined;
  onSwapScheduleEntry?: ScenarioTimelineProps['onSwapScheduleEntry'];
  onSwapPortCall?: ScenarioTimelineProps['onSwapPortCall'];
  onToggleShipment?: TimelineToggle['onToggleShipment'];
  organizations: ScenarioTimelineProps['organizations'];
  isToggleExpanded: boolean;
  dragging?: boolean;
  enablePortCallUpdates?: boolean;
  enableVoyageCharterUpdates?: boolean;
  originalContract?: string;
  combinedDiff?: TimelineDiffState;
  scenarioType?: ScenarioType;
  tceData?: ReturnType<typeof computeTceForTimeData>;
  metrics?: VoyagePerformanceMetrics;
  isFirstShipmentEvent: boolean;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineCompletedVoyage = Omit<TimelineItemBase, 'groupObj'> & {
  type: ItemType.COMPLETED;
  id: string;
  cargo: string;
  isShadow: false;
  isCollapsed: true;
  portCalls: string[];
  orders: ScenarioOrder[];
  isShared?: boolean;
  combinedDiff?: TimelineDiffState;
};

export type TimelineEvent = TimelineItemBase & {
  type: ItemType.EVENT;
  eventType: SingleEventType;
  vessel: Vessel;
  selected?: boolean;
  dragging?: boolean;
  timeData: TimeData<SingleEvent>;
  locationName: string;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  portCallEvents?: Record<PortCallEventType, TimeData<PortCallEvent>>;
};

export type TimelineOffHireEvent = TimelineItemBase & {
  type: ItemType.OFFHIRE;
  vessel: Vessel;
  timeData: TimeData<OffHireEvent>;
  locationAName: string;
  locationBName: string;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineTcInEvent = TimelineItemBase & {
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
  isLastScheduleItem?: boolean;
  timeData: TimeData<StaticEvent>;
  type: ItemType.TC_IN;
};

export type TimelineOpenPosition = TimelineItemBase & {
  type: ItemType.OPEN;
  openPosition: OpenPositionsFragment;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineVesselReservation = TimelineItemBase & {
  type: ItemType.RESERVATION;
  vesselReservation: VesselReservationFragment;
  isLastScheduleItem?: boolean;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type TimelineSuggestion = TimelineItemBase & {
  type: ItemType.SUGGESTION;
  suggestion: Suggestion;
  /** Suggestion may be expanded; only render the given entries from the whole suggestion */
  entries: TCOrderEntry[];
  onSelect: ScenarioTimelineProps['onSuggestionSelect'];
  onDismiss: ScenarioTimelineProps['onDismissSuggestion'];
  start: number;
  end: number;
  suggestionIndex: number;
  vesselId: number;
  isBestFound?: boolean;
  onToggleShipment?: TimelineToggle['onToggleShipment'];
  isToggleExpanded: boolean;
  showInquiries: boolean;
  scheduleContext: ScenarioTimelineProps['scheduleContext'];
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

type TimelineToggle = TimelineItemBase & {
  isToggleExpanded: boolean;
  onToggleShipment: (isCollapsed: boolean) => void;
  groupObj: TimelineGroup<ScenarioTimelineGroupFields>;
};

export type ScenarioTimelineGroupFields = {
  stackItems?: boolean;
  timeline: EventTimeline & {
    isCollapsed?: boolean;
    canCollapse?: boolean;
    coaId?: number;
    coa?: ScenarioTimeline_CoaFragment;
  };
  suggestions: Suggestion[];
};

export type ScenarioTimelineItem = TimelineItem<
  | TimelineVoyage
  | TimelineCompletedVoyage
  | TimelineEvent
  | TimelineSuggestion
  | TimelineOffHireEvent
  | TimelineOpenPosition
  | TimelineVesselReservation
  | TimelineTcInEvent
>;

export type ScenarioTimelineGroup = TimelineGroup<ScenarioTimelineGroupFields>;

export type IndicatorData = {
  type:
    | 'ballast'
    | 'laden'
    | 'load'
    | 'discharge'
    | 'idle'
    | 'single'
    | 'portCall';
  subtype?: string;
  rowType?: string;
  startTime: number;
  duration: number;
  orderId?: number;
  assortments?: Array<{
    assortmentName?: string;
    duration: number;
    amount: number;
    amountUnit: string;
    orderId?: number;
  }>;
} & Pick<
  IndicatorTooltipContent,
  | 'portName'
  | 'assortmentName'
  | 'timezone'
  | 'from'
  | 'to'
  | 'amount'
  | 'amountUnit'
  | 'totalDuration'
>;

export type ViolationIndicatorData = {
  type: 'load_violation' | 'discharge_violation';
  startTime: number;
  duration: number;
  portName: string;
  locationId: number;
  voyageCharterId?: number;
};

export interface VoyageAssortment {
  assortmentName: string;
  amount: number;
  amountUnit: string;
  incomingContractType: IncomingContractType | null;
  cargoDeckOption: string | null;
}

export interface VoyagePortCall {
  canSwapLeftShipments: Array<ScenarioOrder['shipmentId']>;
  canSwapRightShipments: Array<ScenarioOrder['shipmentId']>;
  clientId: ClientId | null;
  events: TimeData[];
  hasCargoEvents: boolean;
  portId: number;
  portName: string;
  maxDwt: number | null;
  portCallFiles: { id: number; note?: string | null; type: PortCallDocument }[];
  startTime: number;
  orderId?: number;
  start?: number;
}

export interface VoyageLeg {
  originPortCallId: ClientId;
  originPortName: string;
  destinationPortCallId: ClientId;
  destinationPortName: string;
  availableMassCapacity: number | null;
  availableMassCapacityPercentage: number | null;
  availableVolumeCapacity: number | null;
  availableVolumeCapacityPercentage: number | null;
  /** According to min margins */
  overCapacityMass: number;
  /** According to min margins */
  overCapacityVolume: number;
  hasUnallocatedOrders: boolean;
}

export type ShipmentIntegrationStatus = null | {
  scenarioDiffersFromIntegration: Set<ScenarioOrder['shipmentId']>;
  actualOrderIdsByShipment: Map<ScenarioOrder['shipmentId'], Set<number>>;
  expectedOrderIdsByShipment: Map<ScenarioOrder['shipmentId'], Set<number>>;
  extraOrderIdsByShipment: DefaultMap<ScenarioOrder['shipmentId'], number[]>;
  missingOrderIdsByShipment: DefaultMap<ScenarioOrder['shipmentId'], number[]>;
};

export type ScheduleItem = ScenarioTimelineItem & {
  type:
    | ItemType.SPOT
    | ItemType.COA
    | ItemType.SCHEDULED_ORDER
    | ItemType.EVENT
    | ItemType.OFFHIRE
    | ItemType.RESERVATION
    | ItemType.OPEN
    | ItemType.TC_IN;
};

export interface IndicatorTooltipContent {
  duration?: number;
  startTime?: number;
  title: string;
  portName?: string;
  assortmentName?: string;
  timezone?: string;
  from?: string;
  to?: string;
  amount?: number;
  amountUnit?: string;
  totalDuration?: number;
  orderId?: number;
  assortments?: Array<{
    assortmentName?: string;
    duration: number;
    amount: number;
    amountUnit: string;
    orderId?: number;
  }>;
  type?: ViolationIndicatorData['type'];
}

export interface VoyageTooltipContent {
  title: string;
  voyageName?: string;
  vesselName?: string;
  vesselOrders: string[];
  vesselVolume?: string;
  vesselCapacity?: string;
  eta?: string;
  etd?: string;
  totalPlannedVolume?: string;
  totalPlannedMass?: string;
  assigner?: string;
  isPortCostMissing?: boolean;
  totalCost?: string;
  vcp?: number;
  externalShipmentId?: string;
}
